import React, { useEffect, useState, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from "../firebaseConfig";
import '../css/admin.css';
const PAYMENT_METHODS = ['Website', 'Cash', 'Venmo'];
const ITEMS = ['Candle', 'T-Shirt'];
const ORDER_TYPES = ['In Person', 'Delivery'];
const ORDER_STATUSES = ['Ordered', 'Shipped', 'Fulfilled'];
const INVENTORY_STATUSES = ['In Stock', 'Out of Stock', 'Pre Order'];

const initialFormState = {
    customer: '',
    email: '',
    quantity: 1,
    total: '',
    item: ITEMS[0],
    paymentMethod: PAYMENT_METHODS[0],
    orderStatus: ORDER_STATUSES[0],
    orderType: ORDER_TYPES[0],

    // Shipping info (stored in Firestore; Stripe can populate later)
    shipping: {
        name: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US',
    },

    // Timeline of status changes. Each entry: { status: string, at: Date | Firestore Timestamp | ISO string }
    statusHistory: [],
};

function Orders({ embedded = false, inventoryOpenToken = 0 }) {
    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState('');
    const [form, setForm] = useState(initialFormState);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Timeline edit modal (for editing a single statusHistory entry timestamp)
    const [isTimelineEditOpen, setIsTimelineEditOpen] = useState(false);
    const [timelineEditIdx, setTimelineEditIdx] = useState(null);
    const [timelineEditDate, setTimelineEditDate] = useState('');
    const [timelineEditTime, setTimelineEditTime] = useState('');
    const [sortPriority, setSortPriority] = useState([]);
    const [isStatsOpen, setIsStatsOpen] = useState(false);
    const [statsRange, setStatsRange] = useState('ytd');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    // Inventory docs live in Firestore under collection `inventory` with doc IDs matching item names.
    // Example for Candle: { status: 'In Stock', allowPickup: true, batches: [{ id, qtyRemaining, createdAt, note }] }
    const [inventoryData, setInventoryData] = useState({}); // e.g., { Candle: {status, allowPickup, batches}, 'T-Shirt': {status} }
    const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
    const [inventoryForm, setInventoryForm] = useState({ item: ITEMS[0], status: INVENTORY_STATUSES[0], allowPickup: false });
    const [inventoryBatchQty, setInventoryBatchQty] = useState('');
    const [inventoryRemoveQty, setInventoryRemoveQty] = useState('');
    const [syncing, setSyncing] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            // Load orders
            const ordersSnapshot = await getDocs(collection(db, 'orders'));
            const ordersData = ordersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

            // Load inventory docs
            const inventorySnapshot = await getDocs(collection(db, 'inventory'));
            const inv = {};
            inventorySnapshot.forEach((d) => {
                inv[d.id] = d.data() || {};
            });

            setOrders(ordersData);
            setInventoryData(inv);
        } catch (err) {
            console.error('Error loading orders or inventory', err);
            setError('Failed to load orders or inventory.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSyncOrders = async () => {
        if (!window.confirm('Sync orders from Stripe?\n\nThis reads recent Stripe payments and adds any candle/shirt orders that are missing. Existing orders are never touched.')) return;
        setSyncing(true);
        try {
            const fn = httpsCallable(getFunctions(), 'syncStripeOrders');
            const res = await fn({ full: false });
            const r = res.data || {};
            if (r.created > 0) {
                await fetchData();
            }
            setToast(`Sync complete — ${r.created || 0} order(s) added${r.skipped ? `, ${r.skipped} already up to date` : ''}${r.errors ? `, ${r.errors} error(s)` : ''}.`);
        } catch (err) {
            console.error('Order sync failed', err);
            setToast(`Sync failed: ${err?.message || 'unknown error'}`);
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(''), 3000);
        return () => clearTimeout(timer);
    }, [toast]);

    // Allow a parent (e.g. the National dashboard) to open the inventory modal.
    useEffect(() => {
        if (inventoryOpenToken) openInventoryModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventoryOpenToken]);

    const resetForm = () => {
        setForm(initialFormState);
        setEditingId(null);
    };

    const openNewOrderModal = () => {
        const today = new Date();
        const initialStatus = initialFormState.orderStatus;
        const initialAt = new Date(today.getFullYear(), today.getMonth(), today.getDate(), today.getHours(), today.getMinutes(), 0, 0);
        setForm({
            ...initialFormState,
            // Seed a first timeline node so brand new orders have history.
            statusHistory: [{ status: initialStatus, at: initialAt }],
        });
        setEditingId(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        closeTimelineEditModal();
        resetForm();
    };

    const openTimelineEditModal = (idx, rawAt) => {
        setTimelineEditIdx(idx);
        setTimelineEditDate(getDateInputValue(rawAt));
        setTimelineEditTime(getTimeInputValue(rawAt));
        setIsTimelineEditOpen(true);
    };

    const closeTimelineEditModal = () => {
        setIsTimelineEditOpen(false);
        setTimelineEditIdx(null);
        setTimelineEditDate('');
        setTimelineEditTime('');
    };

    const handleTimelineEditSave = () => {
        if (timelineEditIdx === null || timelineEditIdx === undefined) return;
        handleUpdateStatusNodeAt(timelineEditIdx, timelineEditDate, timelineEditTime);
        closeTimelineEditModal();
    };

    const openStatsModal = () => {
        setIsStatsOpen(true);
    };

    const openInventoryModal = () => {
        const defaultItem = ITEMS[0];
        const currentDoc = inventoryData[defaultItem] || {};
        const currentStatus = currentDoc.status || INVENTORY_STATUSES[0];
        const allowPickup = !!currentDoc.allowPickup;
        setInventoryForm({ item: defaultItem, status: currentStatus, allowPickup });
        setInventoryBatchQty('');
        setInventoryRemoveQty('');
        setIsInventoryModalOpen(true);
    };

    const closeInventoryModal = () => {
        setIsInventoryModalOpen(false);
    };

    const handleInventoryChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'item') {
            // When switching items, default the fields to whatever is in Firestore (if present)
            const currentDoc = inventoryData[value] || {};
            const currentStatus = currentDoc.status || INVENTORY_STATUSES[0];
            const allowPickup = !!currentDoc.allowPickup;
            setInventoryForm({ item: value, status: currentStatus, allowPickup });
            setInventoryBatchQty('');
            setInventoryRemoveQty('');
            return;
        }

        if (name === 'allowPickup') {
            setInventoryForm((prev) => ({
                ...prev,
                allowPickup: type === 'checkbox' ? checked : !!value,
            }));
            return;
        }

        setInventoryForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleInventorySubmit = async (e) => {
        e.preventDefault();
        setError('');

        const { item, status, allowPickup } = inventoryForm;

        try {
            const invRef = doc(db, 'inventory', item);

            // Merge status always; for Candle, also merge allowPickup.
            const patch = { status };
            if (item === 'Candle') {
                patch.allowPickup = !!allowPickup;
                // Ensure batches exists at least as an array if missing
                const existing = inventoryData?.Candle || {};
                if (!Array.isArray(existing.batches)) {
                    patch.batches = [];
                }
            }

            await setDoc(invRef, patch, { merge: true });

            setInventoryData((prev) => ({
                ...prev,
                [item]: { ...(prev[item] || {}), ...patch },
            }));

            setToast(`Inventory updated for ${item}.`);
            setIsInventoryModalOpen(false);
        } catch (err) {
            console.error('Error updating inventory', err);
            setError('Failed to update inventory.');
        }
    };
    const handleAddNewBatch = async () => {
        if (inventoryForm.item !== 'Candle') return;
        const qty = Number(inventoryBatchQty);
        if (!qty || qty <= 0) {
            window.alert('Enter a positive quantity to add.');
            return;
        }
        try {
            await applyCandleInventoryDelta(qty, 'New batch');
            setToast(`Added new candle batch (+${qty}).`);
            setInventoryBatchQty('');
        } catch (err) {
            console.error('Error adding batch', err);
            setError('Failed to add new batch.');
        }
    };

    const handleRemoveStock = async () => {
        if (inventoryForm.item !== 'Candle') return;
        const qty = Number(inventoryRemoveQty);
        if (!qty || qty <= 0) {
            window.alert('Enter a positive quantity to remove.');
            return;
        }
        const confirmed = window.confirm(`Remove ${qty} candles from inventory?`);
        if (!confirmed) return;
        try {
            await applyCandleInventoryDelta(-qty, 'Manual removal');
            setToast(`Removed candle stock (-${qty}).`);
            setInventoryRemoveQty('');
        } catch (err) {
            console.error('Error removing stock', err);
            setError('Failed to remove stock.');
        }
    };
    // ---- Inventory helpers (Candle batches + stats) ----
    const isCandleItem = (item) => item === 'Candle';

    const normalizeBatches = (batches) => {
        const arr = Array.isArray(batches) ? batches : [];
        // Oldest first (FIFO consumption)
        return [...arr].sort((a, b) => {
            const da = normalizeToDate(a?.createdAt) || new Date(0);
            const db = normalizeToDate(b?.createdAt) || new Date(0);
            return da - db;
        });
    };

    const getCandleTotalInventory = (invData) => {
        const candle = invData?.Candle || {};
        const batches = normalizeBatches(candle.batches);
        return batches.reduce((sum, b) => sum + (Number(b?.qtyRemaining) || 0), 0);
    };

    const getReservedCandleInventory = (ordersList) => {
        // Reserved = Candle quantity for orders that are NOT fulfilled.
        return (ordersList || []).reduce((sum, o) => {
            if (o?.item !== 'Candle') return sum;
            if (o?.orderStatus === 'Fulfilled') return sum;
            const q = typeof o.quantity === 'number' ? o.quantity : Number(o.quantity || 0);
            return sum + (isNaN(q) ? 0 : q);
        }, 0);
    };

    const getNeededSupply = (totalInv, reservedInv) => {
        const diff = (Number(reservedInv) || 0) - (Number(totalInv) || 0);
        return diff > 0 ? diff : 0;
    };

    const buildBatchId = () => `batch_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    const applyCandleInventoryDelta = async (deltaQty, reason = '') => {
        // deltaQty > 0 adds stock (creates a new batch)
        // deltaQty < 0 consumes stock FIFO from oldest batches
        const qty = Number(deltaQty) || 0;
        if (!qty) return;

        const invRef = doc(db, 'inventory', 'Candle');
        const snap = await getDoc(invRef);
        const current = snap.exists() ? (snap.data() || {}) : {};
        const allowPickup = !!current.allowPickup;
        const status = current.status || INVENTORY_STATUSES[0];

        let batches = normalizeBatches(current.batches);

        if (qty > 0) {
            batches.push({
                id: buildBatchId(),
                qtyRemaining: qty,
                createdAt: new Date(),
                note: reason || 'Stock added',
            });
        } else {
            let remainingToConsume = Math.abs(qty);
            const updated = [];
            for (const b of batches) {
                const avail = Number(b?.qtyRemaining) || 0;
                if (remainingToConsume <= 0) {
                    updated.push(b);
                    continue;
                }
                if (avail <= 0) continue;

                const take = Math.min(avail, remainingToConsume);
                const left = avail - take;
                remainingToConsume -= take;

                if (left > 0) {
                    updated.push({ ...b, qtyRemaining: left });
                }
                // If left is 0, batch is fully consumed and removed.
            }
            batches = updated;
        }

        const nextDoc = { status, allowPickup, batches };
        await setDoc(invRef, nextDoc, { merge: true });

        setInventoryData((prev) => ({
            ...prev,
            Candle: { ...(prev.Candle || {}), ...nextDoc },
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: name === 'quantity' ? Number(value) || '' : value,
        }));
    };

    // Update the status dropdown AND reflect it in the timeline immediately, so
    // the change is visible in the modal without having to save and reopen.
    const handleStatusChange = (e) => {
        const nextStatus = e.target.value;
        setForm((prev) => {
            const history = Array.isArray(prev.statusHistory) ? [...prev.statusHistory] : [];
            const sorted = sortStatusHistory(history);
            const lastStatus = sorted.length ? sorted[sorted.length - 1]?.status : null;
            const nextHistory = lastStatus === nextStatus
                ? history
                : [...history, { status: nextStatus, at: new Date() }];
            return { ...prev, orderStatus: nextStatus, statusHistory: nextHistory };
        });
    };

    const handleShippingChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            shipping: {
                ...(prev.shipping || {}),
                [name]: value,
            },
        }));
    };

    const handleDeleteStatusNode = (idx) => {
        setForm((prev) => {
            const history = Array.isArray(prev.statusHistory) ? [...prev.statusHistory] : [];
            if (idx < 0 || idx >= history.length) return prev;
            history.splice(idx, 1);
            return { ...prev, statusHistory: history };
        });
    };

    const handleUpdateStatusNodeAt = (idx, nextDateStr, nextTimeStr) => {
        setForm((prev) => {
            const history = Array.isArray(prev.statusHistory) ? [...prev.statusHistory] : [];
            if (idx < 0 || idx >= history.length) return prev;

            const current = history[idx] || {};

            // Parse date
            let yyyy, mm, dd;
            if (typeof nextDateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(nextDateStr)) {
                [yyyy, mm, dd] = nextDateStr.split('-').map(Number);
            } else {
                // If date missing/invalid, do not update
                return prev;
            }

            // Parse time
            let hours = 0;
            let minutes = 0;
            if (typeof nextTimeStr === 'string' && /^\d{2}:\d{2}$/.test(nextTimeStr)) {
                const [hh, min] = nextTimeStr.split(':');
                hours = Number(hh) || 0;
                minutes = Number(min) || 0;
            }

            const nextAt = new Date(yyyy, mm - 1, dd, hours, minutes, 0, 0);
            history[idx] = { ...current, at: nextAt };
            return { ...prev, statusHistory: history };
        });
    };

    const getDateInputValue = (raw) => {
        const d = normalizeToDate(raw);
        if (!d) return '';
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const getTimeInputValue = (raw) => {
        const d = normalizeToDate(raw);
        if (!d) return '';
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${min}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        let payloadDate = null;

        // Canonical order date = earliest statusHistory timestamp
        // Use whatever is currently in the form, but normalize to an array.
        let nextHistory = Array.isArray(form.statusHistory) ? [...form.statusHistory] : [];
        if (Array.isArray(nextHistory) && nextHistory.length > 0) {
            const sorted = sortStatusHistory(nextHistory);
            payloadDate = normalizeToDate(sorted[0]?.at) || new Date();
        } else {
            payloadDate = new Date();
        }

        // If editing, compare against the existing order in state and record changes.
        const existingOrder = editingId ? orders.find((o) => o.id === editingId) : null;
        const prevStatus = existingOrder?.orderStatus || null;
        const nextStatus = form.orderStatus;
        const prevItem = existingOrder?.item || null;
        const nextItem = form.item;

        const qtyForInventory = Number(form.quantity) || 0;

        // If history is empty (legacy orders), seed it with the previous status if we have one.
        if (nextHistory.length === 0 && prevStatus) {
            nextHistory = [{ status: prevStatus, at: existingOrder?.date || new Date() }];
        }

        // Ensure the timeline ends with the current status. Guard against a
        // duplicate when handleStatusChange has already appended it in the modal.
        const sortedForAppend = sortStatusHistory(nextHistory);
        const lastNodeStatus = sortedForAppend.length
            ? sortedForAppend[sortedForAppend.length - 1]?.status
            : null;
        if (nextStatus && lastNodeStatus !== nextStatus) {
            nextHistory.push({ status: nextStatus, at: new Date() });
        }

        // For brand-new orders, ensure we have at least one status node.
        if (!editingId && nextHistory.length === 0) {
            nextHistory = [{ status: nextStatus || ORDER_STATUSES[0], at: payloadDate instanceof Date ? payloadDate : new Date() }];
        }

        // Canonical order date = the earliest node in the finalized timeline
        // (i.e. when it was placed), and never later than any previously-stored
        // placed date, so marking an order Shipped/Fulfilled can't move it.
        {
            const sortedFinal = sortStatusHistory(nextHistory);
            payloadDate = normalizeToDate(sortedFinal[0]?.at) || payloadDate || new Date();
            const prevDate = normalizeToDate(existingOrder?.date);
            if (prevDate && prevDate < payloadDate) payloadDate = prevDate;
        }

        // ---- Candle inventory adjustments based on fulfillment status transitions ----
        const isPrevCandle = isCandleItem(prevItem);
        const isNextCandle = isCandleItem(nextItem);

        let candleDelta = 0;

        // Brand-new order created as fulfilled => consume now
        if (isNextCandle && !editingId && nextStatus === 'Fulfilled') {
            candleDelta -= qtyForInventory;
        }

        // Fulfilled -> not fulfilled => add back
        if (editingId && isPrevCandle && prevStatus === 'Fulfilled' && nextStatus !== 'Fulfilled') {
            candleDelta += qtyForInventory;
        }

        // Not fulfilled -> fulfilled => consume
        if (editingId && isNextCandle && prevStatus !== 'Fulfilled' && nextStatus === 'Fulfilled') {
            candleDelta -= qtyForInventory;
        }

        // Item changed while fulfilled
        if (editingId && prevStatus === 'Fulfilled' && nextStatus === 'Fulfilled' && prevItem !== nextItem) {
            if (isPrevCandle) candleDelta += qtyForInventory;
            if (isNextCandle) candleDelta -= qtyForInventory;
        }

        // Quantity changed while fulfilled
        if (editingId && isNextCandle && prevStatus === 'Fulfilled' && nextStatus === 'Fulfilled') {
            const prevQty = Number(existingOrder?.quantity) || 0;
            const diff = qtyForInventory - prevQty;
            candleDelta -= diff;
        }

        const payload = {
            ...form,
            date: payloadDate,
            statusHistory: nextHistory,
            // Ensure quantity and total are stored in a consistent way
            quantity: Number(form.quantity) || 0,
            total: form.total === '' ? 0 : Number(form.total),
            // Normalize shipping so Firestore always has a consistent shape
            shipping: {
                name: form?.shipping?.name || '',
                phone: form?.shipping?.phone || '',
                address1: form?.shipping?.address1 || '',
                address2: form?.shipping?.address2 || '',
                city: form?.shipping?.city || '',
                state: form?.shipping?.state || '',
                postalCode: form?.shipping?.postalCode || '',
                country: form?.shipping?.country || 'US',
            },
        };

        try {
            if (candleDelta !== 0) {
                await applyCandleInventoryDelta(candleDelta, 'Order fulfillment adjustment');
            }

            if (editingId) {
                const ref = doc(db, 'orders', editingId);
                await updateDoc(ref, payload);
                setOrders((prev) => prev.map((o) => (o.id === editingId ? { ...o, ...payload } : o)));
                setToast('Order updated.');
            } else {
                const ref = await addDoc(collection(db, 'orders'), payload);
                setOrders((prev) => [{ id: ref.id, ...payload }, ...prev]);
                setToast('New order added.');
            }
            resetForm();
            setIsModalOpen(false);
        } catch (err) {
            console.error('Error saving order', err);
            setError('Failed to save order.');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (order) => {
        setEditingId(order.id);
        setForm({
            customer: order.customer || '',
            email: order.email || '',
            quantity: order.quantity ?? 1,
            total: order.total ?? '',
            item: order.item || ITEMS[0],
            paymentMethod: order.paymentMethod || PAYMENT_METHODS[0],
            orderStatus: order.orderStatus || ORDER_STATUSES[0],
            orderType: order.orderType || ORDER_TYPES[0],
            shipping: {
                name: order?.shipping?.name || order?.shippingName || '',
                phone: order?.shipping?.phone || order?.shippingPhone || '',
                address1: order?.shipping?.address1 || order?.shippingAddress1 || '',
                address2: order?.shipping?.address2 || order?.shippingAddress2 || '',
                city: order?.shipping?.city || order?.shippingCity || '',
                state: order?.shipping?.state || order?.shippingState || '',
                postalCode: order?.shipping?.postalCode || order?.shippingPostalCode || order?.shippingZip || '',
                country: order?.shipping?.country || order?.shippingCountry || 'US',
            },
            // Seed a timeline node for legacy/stripe orders that were saved without
            // history, so the timeline is visible on the very first edit.
            statusHistory: Array.isArray(order.statusHistory) && order.statusHistory.length > 0
                ? order.statusHistory
                : [{ status: order.orderStatus || ORDER_STATUSES[0], at: order.date || new Date() }],
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        const confirmed = window.confirm('Delete this order?');
        if (!confirmed) return;

        try {
            await deleteDoc(doc(db, 'orders', id));
            setOrders((prev) => prev.filter((o) => o.id !== id));
        } catch (err) {
            console.error('Error deleting order', err);
            setError('Failed to delete order.');
        }
    };

    // ---- Status timeline helpers ----
    const normalizeToDate = (raw) => {
        if (!raw) return null;
        if (raw instanceof Date) return raw;
        if (raw && typeof raw.toDate === 'function') return raw.toDate();
        const d = new Date(raw);
        return isNaN(d) ? null : d;
    };

    const formatDateTime = (raw) => {
        const d = normalizeToDate(raw);
        if (!d) return '';

        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);

        let hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        if (hours === 0) hours = 12;

        return `${mm}/${dd}/${yy} ${hours}:${minutes} ${ampm}`;
    };

    const sortStatusHistory = (history) => {
        const arr = Array.isArray(history) ? [...history] : [];
        // Keep stable ordering if timestamps are missing.
        return arr.sort((a, b) => {
            const da = normalizeToDate(a?.at);
            const db = normalizeToDate(b?.at);
            if (!da && !db) return 0;
            if (!da) return 1;
            if (!db) return -1;
            return da - db;
        });
    };

    // Helper to format date for table display as mm/dd/yy and, when available, h:mm AM/PM (local time)
    const formatDate = (raw) => {
        if (!raw) return '';

        // If we stored the value as a plain YYYY-MM-DD string (from the date input)
        // there is no time information, so just show the date.
        if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            const [yyyy, mm, dd] = raw.split('-');
            return `${mm}/${dd}/${yyyy.slice(-2)}`;
        }

        // Normalize to a JS Date (from Firestore Timestamp or other formats)
        let d;
        if (raw && typeof raw.toDate === 'function') {
            d = raw.toDate();
        } else {
            d = new Date(raw);
        }
        if (isNaN(d)) return String(raw);

        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);

        let hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        if (hours === 0) hours = 12;

        return `${mm}/${dd}/${yy} ${hours}:${minutes} ${ampm}`;
    };

    const handleSort = (field) => {
        setSortPriority((prev) => {
            const existingIndex = prev.findIndex((s) => s.field === field);

            if (existingIndex === -1) {
                // New field: append as the NEXT level of sort (subsort)
                // First click = primary, second click = secondary, etc.
                return [...prev, { field, direction: 'asc' }];
            } else {
                // Toggle direction in place, keep the same priority order
                const updated = [...prev];
                const existing = updated[existingIndex];
                updated[existingIndex] = {
                    ...existing,
                    direction: existing.direction === 'asc' ? 'desc' : 'asc',
                };
                return updated;
            }
        });
    };

    // Apply multi-level sorting to orders based on sortPriority (primary, secondary, etc.)
    const sortedOrders = [...orders].sort((a, b) => {
        const getVal = (order, field) => {
            switch (field) {
                case 'customer':
                    return order.customer || '';
                case 'email':
                    return order.email || '';
                case 'quantity':
                    return typeof order.quantity === 'number' ? order.quantity : Number(order.quantity || 0);
                case 'total':
                    return typeof order.total === 'number' ? order.total : Number(order.total || 0);
                case 'item':
                    return order.item || '';
                case 'paymentMethod':
                    return order.paymentMethod || '';
                case 'orderStatus':
                    return order.orderStatus || '';
                case 'orderType':
                    return order.orderType || '';
                case 'date':
                default: {
                    const raw = order.date;
                    if (!raw) return '';
                    if (typeof raw === 'string') return raw; // YYYY-MM-DD sortable lexicographically
                    if (raw instanceof Date) {
                        return raw.toISOString();
                    }
                    if (raw && typeof raw.toDate === 'function') {
                        return raw.toDate().toISOString();
                    }
                    return String(raw);
                }
            }
        };
        const priority = sortPriority.length
            ? sortPriority
            : [{ field: 'date', direction: 'desc' }];
        for (const { field, direction } of priority) {
            const valA = getVal(a, field);
            const valB = getVal(b, field);

            let cmp = 0;
            if (typeof valA === 'number' && typeof valB === 'number') {
                cmp = valA - valB;
            } else {
                const sa = String(valA).toLowerCase();
                const sb = String(valB).toLowerCase();
                if (sa < sb) cmp = -1;
                else if (sa > sb) cmp = 1;
                else cmp = 0;
            }

            if (cmp !== 0) {
                return direction === 'asc' ? cmp : -cmp;
            }
        }

        return 0;
    });
    const filteredOrders = sortedOrders.filter((o) => {
        const term = search.toLowerCase();

        return (
            !term ||
            o.customer?.toLowerCase().includes(term) ||
            o.email?.toLowerCase().includes(term) ||
            formatDate(o.date).toLowerCase().includes(term) ||
            o.item?.toLowerCase().includes(term) ||
            o.paymentMethod?.toLowerCase().includes(term) ||
            o.orderStatus?.toLowerCase().includes(term) ||
            o.orderType?.toLowerCase().includes(term)
        );
    });

    // The date an order was *placed*. Prefer the earliest "Ordered" node in the
    // status timeline, then the earliest node of any kind, then the stored `date`.
    // This keeps sales bucketed by when the customer ordered, never by when the
    // order was later shipped or fulfilled.
    const getOrderPlacedAt = (o) => {
        const history = Array.isArray(o?.statusHistory) ? o.statusHistory : [];
        const dated = history
            .map((h) => ({ status: h?.status, at: normalizeToDate(h?.at) }))
            .filter((h) => h.at);
        if (dated.length) {
            const ordered = dated
                .filter((h) => h.status === 'Ordered')
                .sort((a, b) => a.at - b.at)[0];
            if (ordered) return ordered.at;
            return dated.sort((a, b) => a.at - b.at)[0].at;
        }
        return normalizeToDate(o?.date);
    };

    const getOrderPlacedDay = (o) => {
        const d = getOrderPlacedAt(o);
        if (!d) return null;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };

    // The most recent time an order reached "Fulfilled" (from the timeline).
    const getOrderFulfilledAt = (o) => {
        const history = Array.isArray(o?.statusHistory) ? o.statusHistory : [];
        const fulfilled = history
            .map((h) => ({ status: h?.status, at: normalizeToDate(h?.at) }))
            .filter((h) => h.at && h.status === 'Fulfilled')
            .sort((a, b) => b.at - a.at)[0];
        return fulfilled ? fulfilled.at : null;
    };

    const isShirtItem = (item) => {
        const v = (item || '').trim();
        return v === 'T-Shirt' || v === 'T-shirt' || v === 'T Shirt' || v === 'Shirt';
    };

    const makeEmptyStats = (label) => ({
        label,
        totalOrders: 0,
        totalSales: 0,
        totalCandles: 0,
        totalShirts: 0,
        totalUnits: 0,
        avgOrderValue: 0,
        distinctCustomers: 0,
        byPayment: {},
        candleSales: 0,
        shirtSales: 0,
        // Fulfillment channel (range)
        deliveryOrders: 0,
        deliverySales: 0,
        pickupOrders: 0,
        pickupSales: 0,
        // Status funnel for orders placed in range (by current status)
        statusCounts: { Ordered: 0, Shipped: 0, Fulfilled: 0 },
        // Turnaround: orders placed in range that have since been fulfilled
        fulfilledFromRange: 0,
        avgDaysToFulfill: null,
        // Currently-unfulfilled (all-time, ignores range)
        candleUnfulfilledSales: 0,
        shirtUnfulfilledSales: 0,
        unfulfilledCandleCount: 0,
        unfulfilledShirtCount: 0,
        unfulfilledOrders: 0,
        oldestUnfulfilledDays: null,
    });

    // Resolve a named range to [start, end) local-midnight bounds + a label.
    // Returns null bounds when a custom range is selected without both dates.
    const getRangeBounds = (range) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        let start = new Date(today);
        let end = new Date(today);
        let label = '';

        switch (range) {
            case 'custom':
                if (customStart && customEnd) {
                    const [sy, sm, sd] = customStart.split('-').map(Number);
                    const [ey, em, ed] = customEnd.split('-').map(Number);
                    start = new Date(sy, sm - 1, sd);
                    end = new Date(ey, em - 1, ed);
                    end.setDate(end.getDate() + 1);
                    label = `${customStart} to ${customEnd}`;
                } else {
                    return { start: null, end: null, label: 'Select dates' };
                }
                break;
            case 'week':
                start = new Date(today);
                start.setDate(start.getDate() - 6); // last 7 days including today
                end = new Date(today);
                end.setDate(end.getDate() + 1);
                label = 'Last 7 days';
                break;
            case 'month':
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                label = 'This month';
                break;
            case 'ytd':
                start = new Date(today.getFullYear(), 0, 1);
                end = new Date(today);
                end.setDate(end.getDate() + 1);
                label = 'Year to date';
                break;
            case 'today':
            default:
                start = new Date(today);
                end = new Date(today);
                end.setDate(end.getDate() + 1);
                label = 'Today';
                break;
        }

        return { start, end, label };
    };

    // Orders whose *placed* day falls inside the given range, newest first.
    const getOrdersInRange = (range) => {
        const { start, end } = getRangeBounds(range);
        if (!start || !end) return [];
        return orders
            .filter((o) => {
                const d = getOrderPlacedDay(o);
                return d && d >= start && d < end;
            })
            .sort((a, b) => {
                const da = getOrderPlacedAt(a) || new Date(0);
                const db2 = getOrderPlacedAt(b) || new Date(0);
                return db2 - da;
            });
    };

    const getStatsForRange = (range) => {
        const { start, end, label } = getRangeBounds(range);
        if (!start || !end) return makeEmptyStats(label);

        const stats = makeEmptyStats(label);
        const customerKeys = new Set();
        const fulfillDurations = [];
        let oldestUnfulfilledPlaced = null;

        orders.forEach((o) => {
            const d = getOrderPlacedDay(o);

            const total = typeof o.total === 'number' ? o.total : Number(o.total || 0);
            const safeTotal = isNaN(total) ? 0 : total;
            const quantity = typeof o.quantity === 'number' ? o.quantity : Number(o.quantity || 0);
            const safeQty = isNaN(quantity) ? 0 : quantity;

            const isCandle = o.item === 'Candle';
            const isShirt = isShirtItem(o.item);
            const isUnfulfilled = o.orderStatus !== 'Fulfilled';

            // ---- 1) CURRENTLY-UNFULFILLED METRICS: IGNORE DATE RANGE ----
            if (isUnfulfilled) {
                stats.unfulfilledOrders += 1;
                if (isCandle) {
                    stats.candleUnfulfilledSales += safeTotal;
                    stats.unfulfilledCandleCount += safeQty;
                } else if (isShirt) {
                    stats.shirtUnfulfilledSales += safeTotal;
                    stats.unfulfilledShirtCount += safeQty;
                }
                const placed = getOrderPlacedAt(o);
                if (placed && (!oldestUnfulfilledPlaced || placed < oldestUnfulfilledPlaced)) {
                    oldestUnfulfilledPlaced = placed;
                }
            }

            // ---- 2) RANGE-BASED METRICS: BUCKETED BY DATE PLACED ----
            if (!d) return;
            if (d < start || d >= end) return;

            stats.totalOrders += 1;
            stats.totalSales += safeTotal;
            stats.totalUnits += safeQty;

            const custKey = (o.email || '').trim().toLowerCase() || (o.customer || '').trim().toLowerCase();
            if (custKey) customerKeys.add(custKey);

            if (isCandle) {
                stats.totalCandles += safeQty;
                stats.candleSales += safeTotal;
            } else if (isShirt) {
                stats.totalShirts += safeQty;
                stats.shirtSales += safeTotal;
            }

            // Fulfillment channel
            if ((o.orderType || '') === 'Delivery') {
                stats.deliveryOrders += 1;
                stats.deliverySales += safeTotal;
            } else {
                stats.pickupOrders += 1;
                stats.pickupSales += safeTotal;
            }

            // Status funnel (current status of orders placed in range)
            if (Object.prototype.hasOwnProperty.call(stats.statusCounts, o.orderStatus)) {
                stats.statusCounts[o.orderStatus] += 1;
            }

            // Turnaround for orders placed in range that have been fulfilled
            const fulfilledAt = getOrderFulfilledAt(o);
            const placedAt = getOrderPlacedAt(o);
            if (fulfilledAt && placedAt && fulfilledAt >= placedAt) {
                stats.fulfilledFromRange += 1;
                fulfillDurations.push((fulfilledAt - placedAt) / (1000 * 60 * 60 * 24));
            }

            const method = o.paymentMethod || 'Other';
            if (!stats.byPayment[method]) {
                stats.byPayment[method] = { orders: 0, sales: 0 };
            }
            stats.byPayment[method].orders += 1;
            stats.byPayment[method].sales += safeTotal;
        });

        stats.distinctCustomers = customerKeys.size;

        if (stats.totalOrders > 0) {
            stats.avgOrderValue = stats.totalSales / stats.totalOrders;
        }
        if (fulfillDurations.length > 0) {
            stats.avgDaysToFulfill =
                fulfillDurations.reduce((a, b) => a + b, 0) / fulfillDurations.length;
        }
        if (oldestUnfulfilledPlaced) {
            stats.oldestUnfulfilledDays = Math.floor(
                (Date.now() - oldestUnfulfilledPlaced.getTime()) / (1000 * 60 * 60 * 24)
            );
        }

        return stats;
    };

    const stats = getStatsForRange(statsRange);
    const maxProductSales = Math.max(
        stats.candleSales || 0,
        stats.shirtSales || 0,
        1
    );

    const money = (n) => Number(n || 0).toFixed(2);
    const round1 = (n) => (n == null ? '' : Math.round(n * 10) / 10);

    // Build and download an .xlsx workbook for the currently selected range:
    //   • "Summary"    – the KPI cards as rows
    //   • "Orders"     – one row per order placed in the range
    //   • "By Payment" – totals per payment method
    const exportSalesReport = async () => {
        try {
            // Load SheetJS on demand so it stays out of the main bundle.
            const mod = await import('xlsx');
            const XLSX = mod.default || mod;
            const rangeOrders = getOrdersInRange(statsRange);

            const summaryRows = [
                ['SOLE Sales Report'],
                ['Range', stats.label],
                ['Generated', new Date().toLocaleString()],
                [],
                ['Metric', 'Value'],
                ['Total sales ($)', money(stats.totalSales)],
                ['Total orders', stats.totalOrders],
                ['Avg. order value ($)', money(stats.avgOrderValue)],
                ['Distinct customers', stats.distinctCustomers],
                ['Units sold', stats.totalUnits],
                ['Candles sold', stats.totalCandles],
                ['T-Shirts sold', stats.totalShirts],
                ['Candle sales ($)', money(stats.candleSales)],
                ['T-Shirt sales ($)', money(stats.shirtSales)],
                [],
                ['Delivery orders', stats.deliveryOrders],
                ['Delivery sales ($)', money(stats.deliverySales)],
                ['In-person orders', stats.pickupOrders],
                ['In-person sales ($)', money(stats.pickupSales)],
                [],
                ['Placed in range — now Ordered', stats.statusCounts.Ordered],
                ['Placed in range — now Shipped', stats.statusCounts.Shipped],
                ['Placed in range — now Fulfilled', stats.statusCounts.Fulfilled],
                ['Avg. days to fulfill', stats.avgDaysToFulfill == null ? 'n/a' : round1(stats.avgDaysToFulfill)],
                [],
                ['Unfulfilled orders (all-time)', stats.unfulfilledOrders],
                ['Unfulfilled candle sales ($)', money(stats.candleUnfulfilledSales)],
                ['Unfulfilled T-Shirt sales ($)', money(stats.shirtUnfulfilledSales)],
                ['Unfulfilled candle units', stats.unfulfilledCandleCount],
                ['Unfulfilled T-Shirt units', stats.unfulfilledShirtCount],
                ['Oldest unfulfilled order (days)', stats.oldestUnfulfilledDays == null ? 'n/a' : stats.oldestUnfulfilledDays],
            ];

            const orderRows = rangeOrders.map((o) => {
                const placed = getOrderPlacedAt(o);
                const fulfilled = getOrderFulfilledAt(o);
                const s = o.shipping || {};
                const daysToFulfill = placed && fulfilled && fulfilled >= placed
                    ? round1((fulfilled - placed) / (1000 * 60 * 60 * 24))
                    : '';
                return {
                    'Date Placed': placed ? placed.toLocaleString() : '',
                    Customer: o.customer || '',
                    Email: o.email || '',
                    Phone: o.phone || s.phone || '',
                    Item: o.item || '',
                    Qty: typeof o.quantity === 'number' ? o.quantity : Number(o.quantity || 0),
                    'Total ($)': money(o.total),
                    Payment: o.paymentMethod || '',
                    Status: o.orderStatus || '',
                    Type: o.orderType || '',
                    'Ship To': [s.name, s.address1, s.address2, s.city, s.state, s.postalCode]
                        .filter(Boolean)
                        .join(', '),
                    'Date Fulfilled': fulfilled ? fulfilled.toLocaleString() : '',
                    'Days to Fulfill': daysToFulfill,
                };
            });

            const paymentRows = Object.entries(stats.byPayment).map(([method, s]) => ({
                Method: method,
                Orders: s.orders,
                'Sales ($)': money(s.sales),
            }));

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');
            XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.json_to_sheet(orderRows.length ? orderRows : [{ 'Date Placed': 'No orders in range' }]),
                'Orders'
            );
            if (paymentRows.length) {
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(paymentRows), 'By Payment');
            }

            const stamp = new Date().toISOString().slice(0, 10);
            const safeLabel = (stats.label || 'range').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
            XLSX.writeFile(wb, `sole-sales-${safeLabel}-${stamp}.xlsx`);
        } catch (err) {
            console.error('Failed to export sales report', err);
            setError('Could not generate the Excel report.');
        }
    };

    return (
        <div className={embedded ? 'orders-embedded-root' : ''}>
            {!embedded && <div />}
            <div className={`orders-page${embedded ? ' orders-page--embedded' : ''}`}>
                {error && (
                    <div style={{ color: 'red', marginBottom: '0.75rem' }}>
                        {error}
                    </div>
                )}
                {toast && (
                    <div
                        style={{
                            marginBottom: '0.75rem',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '0.5rem',
                            backgroundColor: '#ecfdf3',
                            color: '#166534',
                            border: '1px solid #bbf7d0',
                            fontSize: '0.85rem',
                        }}
                    >
                        {toast}
                    </div>
                )}

                <section>
                    <div className="orders-header-row">
                        <h2>All Orders</h2>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button type="button" onClick={openStatsModal}>
                                Sales
                            </button>
                            <button type="button" onClick={openInventoryModal}>
                                Edit Inventory
                            </button>
                            <button type="button" onClick={handleSyncOrders} disabled={syncing}>
                                {syncing ? 'Syncing…' : 'Sync from Stripe'}
                            </button>
                            <button type="button" onClick={openNewOrderModal}>
                                Add Order
                            </button>
                        </div>
                    </div>
                    <input
                        type="text"
                        placeholder="Search orders…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="orders-search-input"
                    />
                    {loading ? (
                        <p>Loading orders…</p>
                    ) : orders.length === 0 ? (
                        <p>No orders yet.</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="orders-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort('date')}>Date</th>
                                        <th onClick={() => handleSort('customer')}>Customer</th>
                                        <th onClick={() => handleSort('email')}>Email</th>
                                        <th onClick={() => handleSort('quantity')}>Quantity</th>
                                        <th onClick={() => handleSort('total')}>Total</th>
                                        <th onClick={() => handleSort('item')}>Item</th>
                                        <th onClick={() => handleSort('paymentMethod')}>Payment</th>
                                        <th onClick={() => handleSort('orderStatus')}>Status</th>
                                        <th onClick={() => handleSort('orderType')}>Type</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOrders.map((order) => (
                                        <tr
                                            key={order.id}
                                            className={
                                                order.orderStatus === 'Shipped'
                                                    ? 'order-row shipped'
                                                    : order.orderStatus === 'Ordered'
                                                        ? 'order-row ordered'
                                                        : 'order-row'
                                            }
                                        >
                                            <td>{formatDate(order.date)}</td>
                                            <td>{order.customer}</td>
                                            <td>{order.email}</td>
                                            <td>{order.quantity}</td>
                                            <td>{typeof order.total === 'number' ? `$${order.total.toFixed(2)}` : order.total}</td>
                                            <td>{order.item}</td>
                                            <td>{order.paymentMethod}</td>
                                            <td>{order.orderStatus}</td>
                                            <td>{order.orderType}</td>
                                            <td>
                                                <button type="button" onClick={() => handleEdit(order)} style={{ marginRight: '0.5rem' }}>
                                                    Edit
                                                </button>
                                                <button type="button" onClick={() => handleDelete(order.id)}>
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {isInventoryModalOpen && (
                    <div className="orders-modal-backdrop">
                        <div className="orders-modal" style={{ maxWidth: '520px' }}>
                            <h2>Edit Inventory</h2>
                            <form onSubmit={handleInventorySubmit} className="orders-inventory-form" style={{ margin: '0 auto' }}>
                                <div className="orders-inventory-form-body" style={{ width: '100%', maxWidth: '420px', margin: '0 auto' }}>
                                    <div className="orders-inventory-top-row" style={{ width: '100%' }}>
                                        <label>
                                            Item
                                            <select
                                                name="item"
                                                value={inventoryForm.item}
                                                onChange={handleInventoryChange}
                                            >
                                                {ITEMS.map((opt) => (
                                                    <option key={opt} value={opt}>
                                                        {opt}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label>
                                            Status
                                            <select
                                                name="status"
                                                value={inventoryForm.status}
                                                onChange={handleInventoryChange}
                                            >
                                                {INVENTORY_STATUSES.map((status) => (
                                                    <option key={status} value={status}>
                                                        {status}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>

                                    {/* Candle-only inventory controls */}
                                    {inventoryForm.item === 'Candle' && (
                                        <div className="orders-inventory-panel">
                                            <div className="orders-inventory-panel-title">Candle inventory</div>

                                            <label className="orders-inventory-toggle">
                                                <input
                                                    type="checkbox"
                                                    name="allowPickup"
                                                    checked={!!inventoryForm.allowPickup}
                                                    onChange={handleInventoryChange}
                                                />
                                                Allow in-person pickup
                                            </label>


                                            <div className="orders-inventory-actions">
                                                <div className="orders-inventory-action-row">
                                                    <input
                                                        className="orders-inventory-qty"
                                                        type="number"
                                                        min="1"
                                                        placeholder="Qty to add"
                                                        value={inventoryBatchQty}
                                                        onChange={(e) => setInventoryBatchQty(e.target.value)}
                                                    />
                                                    <button type="button" onClick={handleAddNewBatch}>
                                                        Add batch
                                                    </button>
                                                </div>

                                                <div className="orders-inventory-action-row">
                                                    <input
                                                        className="orders-inventory-qty"
                                                        type="number"
                                                        min="1"
                                                        placeholder="Qty to remove"
                                                        value={inventoryRemoveQty}
                                                        onChange={(e) => setInventoryRemoveQty(e.target.value)}
                                                    />
                                                    <button type="button" onClick={handleRemoveStock}>
                                                        Remove stock
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="orders-inventory-stats-grid">
                                                {(() => {
                                                    const totalInv = getCandleTotalInventory(inventoryData);
                                                    const reservedInv = getReservedCandleInventory(orders);
                                                    const needed = getNeededSupply(totalInv, reservedInv);
                                                    const excess = Math.max((Number(totalInv) || 0) - (Number(reservedInv) || 0), 0);
                                                    return (
                                                        <>
                                                            <div className="orders-inventory-stats-item">
                                                                <div className="orders-inventory-stats-label">Total inventory</div>
                                                                <div className="orders-inventory-stats-value">{totalInv}</div>
                                                            </div>
                                                            <div className="orders-inventory-stats-item">
                                                                <div className="orders-inventory-stats-label">Reserved</div>
                                                                <div className="orders-inventory-stats-value">{reservedInv}</div>
                                                            </div>
                                                            <div className="orders-inventory-stats-item">
                                                                <div className="orders-inventory-stats-label">Excess supply</div>
                                                                <div className="orders-inventory-stats-value">{excess}</div>
                                                            </div>
                                                            <div className="orders-inventory-stats-item">
                                                                <div className="orders-inventory-stats-label">Needed supply</div>
                                                                <div className="orders-inventory-stats-value">{needed}</div>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="orders-modal-actions" style={{ justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                                    <button type="submit">
                                        Update Inventory
                                    </button>
                                    <button type="button" onClick={closeInventoryModal}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {isModalOpen && (
                    <div className="orders-modal-backdrop">
                        <div className="orders-modal">
                            <h2>{editingId ? 'Edit Order' : 'New Order'}</h2>
                            <form onSubmit={handleSubmit} className="orders-form">
                                <label>
                                    Customer
                                    <input
                                        type="text"
                                        name="customer"
                                        value={form.customer}
                                        onChange={handleChange}
                                        required
                                    />
                                </label>
                                <label>
                                    Email
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                    />
                                </label>
                                <label>
                                    Quantity
                                    <input
                                        type="number"
                                        name="quantity"
                                        min="1"
                                        value={form.quantity}
                                        onChange={handleChange}
                                        required
                                    />
                                </label>
                                <label>
                                    Total ($)
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="total"
                                        value={form.total}
                                        onChange={handleChange}
                                        required
                                    />
                                </label>
                                <label>
                                    Item
                                    <select name="item" value={form.item} onChange={handleChange}>
                                        {ITEMS.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    Payment Method
                                    <select name="paymentMethod" value={form.paymentMethod} onChange={handleChange}>
                                        {PAYMENT_METHODS.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    Order Status
                                    <select name="orderStatus" value={form.orderStatus} onChange={handleStatusChange}>
                                        {ORDER_STATUSES.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {/* Status Timeline UI */}
                                <div style={{ marginTop: '0.25rem' }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                                        Status timeline
                                    </div>
                                    {Array.isArray(form.statusHistory) && form.statusHistory.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                            {sortStatusHistory(form.statusHistory).map((node, i) => {
                                                // Find the matching node in the underlying (unsorted) array so edits persist correctly.
                                                const rawHistory = Array.isArray(form.statusHistory) ? form.statusHistory : [];
                                                const rawIdx = rawHistory.findIndex((h) => h?.status === node?.status && String(h?.at) === String(node?.at));
                                                const idx = rawIdx === -1 ? i : rawIdx;

                                                // const dateVal = getDateInputValue(node?.at);
                                                // const timeVal = getTimeInputValue(node?.at);

                                                return (
                                                    <div
                                                        key={`${node?.status || 'status'}-${i}`}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: '0.75rem',
                                                            padding: '0.4rem 0.55rem',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '0.5rem',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: 0 }}>
                                                            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{node?.status || '—'}</span>
                                                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                                    {formatDateTime(node?.at)}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openTimelineEditModal(idx, node?.at)}
                                                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
                                                                >
                                                                    Edit
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const confirmed = window.confirm('Delete this timeline entry?');
                                                                if (!confirmed) return;
                                                                handleDeleteStatusNode(idx);
                                                            }}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                            No timeline entries yet.
                                        </div>
                                    )}
                                </div>
                                <label>
                                    Order Type
                                    <select name="orderType" value={form.orderType} onChange={handleChange}>
                                        {ORDER_TYPES.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                {form.orderType === 'Delivery' && (
                                    <div className="orders-shipping-section">
                                        <div className="orders-section-title">Shipping</div>

                                        <label>
                                            Recipient name
                                            <input
                                                type="text"
                                                name="name"
                                                value={form?.shipping?.name || ''}
                                                onChange={handleShippingChange}
                                            />
                                        </label>

                                        <label>
                                            Phone
                                            <input
                                                type="text"
                                                name="phone"
                                                value={form?.shipping?.phone || ''}
                                                onChange={handleShippingChange}
                                            />
                                        </label>

                                        <label className="orders-shipping-span-2">
                                            Address line 1
                                            <input
                                                type="text"
                                                name="address1"
                                                value={form?.shipping?.address1 || ''}
                                                onChange={handleShippingChange}
                                            />
                                        </label>

                                        <label className="orders-shipping-span-2">
                                            Address line 2
                                            <input
                                                type="text"
                                                name="address2"
                                                value={form?.shipping?.address2 || ''}
                                                onChange={handleShippingChange}
                                            />
                                        </label>

                                        <label>
                                            City
                                            <input
                                                type="text"
                                                name="city"
                                                value={form?.shipping?.city || ''}
                                                onChange={handleShippingChange}
                                            />
                                        </label>

                                        <label>
                                            State
                                            <input
                                                type="text"
                                                name="state"
                                                value={form?.shipping?.state || ''}
                                                onChange={handleShippingChange}
                                            />
                                        </label>

                                        <label>
                                            Postal code
                                            <input
                                                type="text"
                                                name="postalCode"
                                                value={form?.shipping?.postalCode || ''}
                                                onChange={handleShippingChange}
                                            />
                                        </label>

                                        <label>
                                            Country
                                            <input
                                                type="text"
                                                name="country"
                                                value={form?.shipping?.country || 'US'}
                                                onChange={handleShippingChange}
                                            />
                                        </label>
                                    </div>
                                )}

                                <div className="orders-modal-actions">
                                    <button type="submit" disabled={saving}>
                                        {saving ? 'Saving…' : editingId ? 'Update Order' : 'Add Order'}
                                    </button>
                                    <button type="button" onClick={closeModal}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                            {isTimelineEditOpen && (
                                <div
                                    className="orders-modal-backdrop"
                                    style={{ backgroundColor: 'rgba(15, 23, 42, 0.25)', alignItems: 'center' }}
                                >
                                    <div className="orders-modal" style={{ maxWidth: '420px', maxHeight: 'unset' }}>
                                        <h2 style={{ marginBottom: '0.5rem' }}>Edit timeline entry</h2>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                            <label>
                                                Date
                                                <input
                                                    type="date"
                                                    value={timelineEditDate}
                                                    onChange={(e) => setTimelineEditDate(e.target.value)}
                                                />
                                            </label>
                                            <label>
                                                Time
                                                <input
                                                    type="time"
                                                    value={timelineEditTime}
                                                    onChange={(e) => setTimelineEditTime(e.target.value)}
                                                />
                                            </label>
                                        </div>

                                        <div className="orders-modal-actions">
                                            <button type="button" onClick={handleTimelineEditSave}>
                                                Save
                                            </button>
                                            <button type="button" onClick={closeTimelineEditModal}>
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {isStatsOpen && (
                    <div className="orders-modal-backdrop">
                        <div className="orders-modal">
                            <h2>Sales Summary</h2>

                            <div className="orders-modal-actions" style={{ justifyContent: 'flex-start', marginBottom: '0.75rem' }}>
                                <button type="button" onClick={() => setStatsRange('today')}>
                                    Today
                                </button>
                                <button type="button" onClick={() => setStatsRange('week')}>
                                    This Week
                                </button>
                                <button type="button" onClick={() => setStatsRange('month')}>
                                    This Month
                                </button>
                                <button type="button" onClick={() => setStatsRange('ytd')}>
                                    YTD
                                </button>
                            </div>

                            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                <label>
                                    Start
                                    <input
                                        type="date"
                                        value={customStart}
                                        onChange={(e) => setCustomStart(e.target.value)}
                                    />
                                </label>
                                <label>
                                    End
                                    <input
                                        type="date"
                                        value={customEnd}
                                        onChange={(e) => setCustomEnd(e.target.value)}
                                    />
                                </label>
                                <button type="button" onClick={() => setStatsRange('custom')}>
                                    Apply Range
                                </button>
                            </div>

                            <div className="orders-stats-range">Range: {stats.label}</div>

                            <div className="orders-stats-grid">
                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Total sales</div>
                                    <div className="orders-stats-value">
                                        ${stats.totalSales.toFixed(2)}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Total orders</div>
                                    <div className="orders-stats-value">
                                        {stats.totalOrders}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Avg. order value</div>
                                    <div className="orders-stats-value">
                                        ${stats.avgOrderValue.toFixed(2)}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Candles sold</div>
                                    <div className="orders-stats-value">
                                        {stats.totalCandles}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Shirts sold</div>
                                    <div className="orders-stats-value">
                                        {stats.totalShirts}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Candle sales</div>
                                    <div className="orders-stats-value">
                                        ${stats.candleSales.toFixed(2)}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">T-Shirt sales</div>
                                    <div className="orders-stats-value">
                                        ${stats.shirtSales.toFixed(2)}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Unfulfilled candle sales</div>
                                    <div className="orders-stats-value">
                                        ${stats.candleUnfulfilledSales.toFixed(2)}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Unfulfilled T-Shirt sales</div>
                                    <div className="orders-stats-value">
                                        ${stats.shirtUnfulfilledSales.toFixed(2)}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Unfulfilled candle count</div>
                                    <div className="orders-stats-value">
                                        {stats.unfulfilledCandleCount}
                                    </div>
                                </div>
                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Unfulfilled T-Shirt count</div>
                                    <div className="orders-stats-value">
                                        {stats.unfulfilledShirtCount}
                                    </div>
                                </div>

                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Distinct customers</div>
                                    <div className="orders-stats-value">
                                        {stats.distinctCustomers}
                                    </div>
                                </div>
                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Units sold</div>
                                    <div className="orders-stats-value">
                                        {stats.totalUnits}
                                    </div>
                                </div>
                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Unfulfilled orders (all-time)</div>
                                    <div className="orders-stats-value">
                                        {stats.unfulfilledOrders}
                                    </div>
                                </div>
                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Oldest unfulfilled</div>
                                    <div className="orders-stats-value">
                                        {stats.oldestUnfulfilledDays == null ? '—' : `${stats.oldestUnfulfilledDays}d`}
                                    </div>
                                </div>
                                <div className="orders-stats-card">
                                    <div className="orders-stats-label">Avg. days to fulfill</div>
                                    <div className="orders-stats-value">
                                        {stats.avgDaysToFulfill == null ? '—' : (Math.round(stats.avgDaysToFulfill * 10) / 10)}
                                    </div>
                                </div>
                            </div>

                            <div className="orders-stats-section">
                                <h3>Fulfillment</h3>
                                <table className="orders-stats-payment-table">
                                    <tbody>
                                        <tr>
                                            <td>Delivery orders</td>
                                            <td>{stats.deliveryOrders}</td>
                                            <td>${stats.deliverySales.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td>In-person orders</td>
                                            <td>{stats.pickupOrders}</td>
                                            <td>${stats.pickupSales.toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#374151' }}>
                                    Placed in this range — currently:&nbsp;
                                    <strong>{stats.statusCounts.Ordered}</strong> Ordered,&nbsp;
                                    <strong>{stats.statusCounts.Shipped}</strong> Shipped,&nbsp;
                                    <strong>{stats.statusCounts.Fulfilled}</strong> Fulfilled
                                </div>
                            </div>

                            <div className="orders-stats-section">
                                <h3>Sales by product</h3>
                                <div className="orders-bar-chart">
                                    <div className="orders-bar-row">
                                        <span className="orders-bar-label">Candles</span>
                                        <div className="orders-bar-track">
                                            <div
                                                className="orders-bar-fill candle"
                                                style={{
                                                    width: `${((stats.candleSales || 0) / maxProductSales) * 100}%`,
                                                }}
                                            />
                                        </div>
                                        <span className="orders-bar-value">
                                            ${stats.candleSales.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="orders-bar-row">
                                        <span className="orders-bar-label">T-Shirts</span>
                                        <div className="orders-bar-track">
                                            <div
                                                className="orders-bar-fill shirt"
                                                style={{
                                                    width: `${((stats.shirtSales || 0) / maxProductSales) * 100}%`,
                                                }}
                                            />
                                        </div>
                                        <span className="orders-bar-value">
                                            ${stats.shirtSales.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {Object.keys(stats.byPayment).length > 0 && (
                                <div className="orders-stats-section">
                                    <h3>By payment method</h3>
                                    <table className="orders-stats-payment-table">
                                        <thead>
                                            <tr>
                                                <th>Method</th>
                                                <th>Orders</th>
                                                <th>Sales</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(stats.byPayment).map(([method, summary]) => (
                                                <tr key={method}>
                                                    <td>{method}</td>
                                                    <td>{summary.orders}</td>
                                                    <td>${summary.sales.toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="orders-modal-actions">
                                <button type="button" onClick={exportSalesReport}>
                                    Export Excel
                                </button>
                                <button type="button" onClick={() => setIsStatsOpen(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Orders;